import React, { createContext, useContext, useState, useEffect, useRef, useCallback, useMemo } from 'react';
import axios from 'axios';

const ConnectivityContext = createContext();

export function useConnectivity() {
  return useContext(ConnectivityContext);
}

export function ConnectivityProvider({ children }) {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [showOfflineAlert, setShowOfflineAlert] = useState(false);
  // Plain refs, not state: the mount-time useEffect below registers
  // checkServerConnectivity with setInterval(..., []) once and never again,
  // so that interval forever calls the closure captured at mount. Reading
  // consecutiveFailures/lastCheckTime as state from that stale closure meant
  // they were permanently frozen at their mount-time values (0 and null) no
  // matter how many times the interval fired - every periodic failure
  // computed newFailures = 0 + 1 = 1 and never accumulated to the 3 needed
  // to mark the app offline. Refs are mutated in place and shared by every
  // closure (stale or not), so they always reflect the latest value.
  const lastCheckTimeRef = useRef(null);
  const consecutiveFailuresRef = useRef(0);

  // Function to check connectivity by making a small request
  const checkServerConnectivity = useCallback(async () => {
    try {
      // To avoid frequent checks, don't check if we've checked in the last 10 seconds
      const now = Date.now();
      if (lastCheckTimeRef.current && now - lastCheckTimeRef.current < 10000) {
        return isOnline;
      }

      lastCheckTimeRef.current = now;
      
      // Always use the proxy in development - it will forward to the backend
      const backendUrl = '/api/health-check';
      
      // Retry mechanism for better resilience
      let lastError;
      for (let attempt = 1; attempt <= 2; attempt++) {
        try {
          // Use a small endpoint that just returns 200 OK
          const response = await axios.get(backendUrl, {
            timeout: 3000, // Reduced timeout
            // Don't send credentials for the health check
            withCredentials: false
          });
          
          if (response.status === 200) {
            setIsOnline(true);
            consecutiveFailuresRef.current = 0; // Reset failure counter on success
            return true;
          } else {
            // If response is not 200, we're still online but the server has issues
            setIsOnline(true);
            return true;
          }
        } catch (error) {
          lastError = error;
          // If this is the first attempt, wait a bit before retrying
          if (attempt === 1) {
            await new Promise(resolve => setTimeout(resolve, 500));
          }
        }
      }
      
      // If we get here, both attempts failed
      console.log('Health check failed after retries:', lastError.message);
      
      if (lastError.code === 'ERR_NETWORK' || lastError.code === 'ECONNABORTED' || !lastError.response) {
        // Increment failure counter
        const newFailures = consecutiveFailuresRef.current + 1;
        consecutiveFailuresRef.current = newFailures;
        
        // Only mark as offline after 3 consecutive failures to avoid false positives
        if (newFailures >= 3) {
          setIsOnline(false);
          return false;
        } else {
          // Still consider online for first few failures
          setIsOnline(true);
          return true;
        }
      }
      // If we got an HTTP error response, we're still online
      setIsOnline(true);
      return true;
    } catch (error) {
      console.log('Health check failed:', error.message);
      
      if (error.code === 'ERR_NETWORK' || error.code === 'ECONNABORTED' || !error.response) {
        // Increment failure counter
        const newFailures = consecutiveFailuresRef.current + 1;
        consecutiveFailuresRef.current = newFailures;
        
        // Only mark as offline after 3 consecutive failures to avoid false positives
        if (newFailures >= 3) {
          setIsOnline(false);
          return false;
        } else {
          // Still consider online for first few failures
          setIsOnline(true);
          return true;
        }
      }
      // If we got an HTTP error response, we're still online
      setIsOnline(true);
      return true;
    }
  }, [isOnline]);

  // Handle browser online/offline events
  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      setShowOfflineAlert(false);
      consecutiveFailuresRef.current = 0; // Reset failure counter
      // Try to connect to server when browser says we're online
      checkServerConnectivity();
    };

    const handleOffline = () => {
      setIsOnline(false);
      setShowOfflineAlert(true);
    };

    // Set up event listeners
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    // Initial connectivity check
    checkServerConnectivity();

    // Periodic connectivity check (every 60 seconds instead of 30)
    const intervalId = setInterval(checkServerConnectivity, 60000);

    // Clean up
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
      clearInterval(intervalId);
    };
  }, []); // Safe to run once: consecutiveFailures/lastCheckTime are refs now, not state

  // Memoized so consumers (LandingPage, Login, OfflineAlert) only re-render
  // when isOnline/showOfflineAlert actually change, instead of on every
  // ConnectivityProvider render - matches the fix applied to AuthContext,
  // whose unmemoized value was re-rendering every useAuth() consumer in the
  // app on a much hotter cadence.
  const value = useMemo(() => ({
    isOnline,
    checkServerConnectivity,
    showOfflineAlert,
    setShowOfflineAlert
  }), [isOnline, checkServerConnectivity, showOfflineAlert]);

  return (
    <ConnectivityContext.Provider value={value}>
      {children}
    </ConnectivityContext.Provider>
  );
}
